import { Component, Input, OnInit } from '@angular/core';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { LocationReviewsService, ReviewItemDto } from '../../../services/location-reviews.service';

type ScopeTab = 'location' | 'staff';
type StatusFilter = 'all' | 'published' | 'flagged' | 'hidden';

/**
 * Reviews & Ratings (FAC-53) — admin merchant section in the location
 * settings form. Lets the merchant browse customer reviews of a location
 * (or its staff), respond to them, and moderate flagged or inappropriate
 * entries (publish / hide).
 */
@Component({
  selector: 'app-location-reviews-section',
  templateUrl: './location-reviews-section.component.html',
  styleUrls: ['./location-reviews-section.component.css'],
  standalone: false
})
export class LocationReviewsSectionComponent implements OnInit {
  @Input() locationId!: string | null;

  scope: ScopeTab = 'location';
  statusFilter: StatusFilter = 'all';

  isLoading = false;
  items: ReviewItemDto[] = [];
  total = 0;
  page = 1;
  readonly pageSize = 20;

  // Per-row UI state
  responseBody: { [reviewId: string]: string } = {};
  busyRow: { [reviewId: string]: boolean } = {};
  expandedRow: { [reviewId: string]: boolean } = {};

  constructor(
    private readonly api: LocationReviewsService,
    private readonly notify: NotificationService
  ) {}

  ngOnInit(): void {
    if (this.locationId) {
      this.load();
    }
  }

  selectScope(tab: ScopeTab): void {
    if (this.scope === tab) return;
    this.scope = tab;
    this.page = 1;
    this.load();
  }

  changeStatusFilter(): void {
    this.page = 1;
    this.load();
  }

  goToPage(next: number): void {
    if (next < 1 || next > this.totalPages) return;
    this.page = next;
    this.load();
  }

  get totalPages(): number {
    if (this.total === 0) return 1;
    return Math.ceil(this.total / this.pageSize);
  }

  load(): void {
    if (!this.locationId) return;
    this.isLoading = true;
    const status = this.statusFilter === 'all' ? null : this.statusFilter;
    const obs = this.scope === 'location'
      ? this.api.listLocationReviews(this.locationId, { status: status, page: this.page, pageSize: this.pageSize })
      : this.api.listStaffReviews(this.locationId, { status: status, page: this.page, pageSize: this.pageSize });

    obs.subscribe({
      next: page => {
        this.items = page.items || [];
        this.total = page.total || 0;
        this.isLoading = false;
        // Pre-populate textareas with existing responses for quick edits.
        this.items.forEach(it => {
          this.responseBody[it.reviewId] = it.response ? it.response.body : '';
        });
      },
      error: () => {
        this.notify.errorBottom('No se han podido cargar las reseñas.');
        this.isLoading = false;
      }
    });
  }

  toggleExpand(reviewId: string): void {
    this.expandedRow[reviewId] = !this.expandedRow[reviewId];
  }

  saveResponse(item: ReviewItemDto): void {
    if (!this.locationId) return;
    const body = (this.responseBody[item.reviewId] || '').trim();
    if (!body) {
      this.notify.errorBottom('La respuesta no puede estar vacía.');
      return;
    }
    this.busyRow[item.reviewId] = true;
    this.api.respond(this.locationId, item.reviewId, body).subscribe({
      next: () => {
        this.notify.okBottom('Respuesta publicada.');
        this.busyRow[item.reviewId] = false;
        this.load();
      },
      error: () => {
        this.notify.errorBottom('No se ha podido publicar la respuesta.');
        this.busyRow[item.reviewId] = false;
      }
    });
  }

  deleteResponse(item: ReviewItemDto): void {
    if (!this.locationId || !item.response) return;
    this.busyRow[item.reviewId] = true;
    this.api.deleteResponse(this.locationId, item.reviewId).subscribe({
      next: () => {
        this.notify.okBottom('Respuesta eliminada.');
        this.busyRow[item.reviewId] = false;
        this.load();
      },
      error: () => {
        this.notify.errorBottom('No se ha podido eliminar la respuesta.');
        this.busyRow[item.reviewId] = false;
      }
    });
  }

  setStatus(item: ReviewItemDto, status: 'published' | 'hidden'): void {
    if (!this.locationId) return;
    this.busyRow[item.reviewId] = true;
    const obs = this.scope === 'location'
      ? this.api.setLocationReviewStatus(this.locationId, item.reviewId, status)
      : this.api.setStaffReviewStatus(this.locationId, item.reviewId, status);
    obs.subscribe({
      next: () => {
        this.notify.okBottom(status === 'hidden' ? 'Reseña ocultada.' : 'Reseña publicada.');
        this.busyRow[item.reviewId] = false;
        this.load();
      },
      error: () => {
        this.notify.errorBottom('No se ha podido cambiar el estado de la reseña.');
        this.busyRow[item.reviewId] = false;
      }
    });
  }

  starsArray(rating: number): string[] {
    const out: string[] = [];
    for (let i = 1; i <= 5; i++) {
      out.push(i <= rating ? 'star' : 'star_border');
    }
    return out;
  }
  public trackByIndex(i: number) { return i; }

}
